/**
 * Real Campaign Creation Service
 * Replaces stub implementation with actual Supabase integration
 * Now uses the unified save service for consistent campaign saving
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import { dateUtils } from '@/utils/dateUtils';
import { logError, logWarning, logInfo } from '@/utils/errorManager';
import { saveCampaignUnified, type CampaignSaveData } from './unifiedCampaignSaveService';

// ================ TYPES ================

interface CampaignData {
  companyName: string;
  brandName: string;
  personInCharge: string;
  jobTitle: string;
  email: string;
  mobile: string;
  selectedLocations: any[];
  creativeUrls: string[];
  totalDevices: number;
  durationDays: number;
  startDate: string | Date;
  endDate: string | Date;
  totalBudget: number;
  budgetCalculations: any;
  detailsUrl?: string | null;
  locationsUrl?: string | null;
  termsAccepted: boolean;
  paymentType?: string;
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

interface CampaignResult {
  success: boolean;
  id?: string;
  campaignId?: string;
  error?: Error | null;
}

// ================ VALIDATION ================

/**
 * Validate campaign data before submission
 */
export const validateCampaignData = (data: Partial<CampaignData>): ValidationResult => {
  const errors: string[] = [];

  // Required fields
  if (!data.companyName?.trim()) errors.push('Company name is required');
  if (!data.brandName?.trim()) errors.push('Brand name is required');
  if (!data.personInCharge?.trim()) errors.push('Person in charge is required');
  if (!data.jobTitle?.trim()) errors.push('Job title is required');
  if (!data.email?.trim()) errors.push('Email is required');
  if (!data.mobile?.trim()) errors.push('Mobile number is required');

  // Email validation
  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.push('Invalid email address');
  }

  // Phone validation
  if (data.mobile && !/^\+?[0-9\s\-()]{8,20}$/.test(data.mobile)) {
    errors.push('Invalid mobile number');
  }

  // Locations
  if (!data.selectedLocations || data.selectedLocations.length === 0) {
    errors.push('At least one location must be selected');
  }

  // Dates
  if (!data.startDate) {
    errors.push('Start date is required');
  } else {
    const validation = dateUtils.validateStartDate(data.startDate);
    if (!validation.valid) {
      errors.push(validation.error || 'Invalid start date');
    }
  }

  if (!data.endDate) {
    errors.push('End date is required');
  }

  if (data.startDate && data.endDate) {
    const rangeValidation = dateUtils.validateDateRange(data.startDate, data.endDate);
    if (!rangeValidation.valid) {
      errors.push(rangeValidation.error || 'Invalid date range');
    }
  }

  // Duration
  if (!data.durationDays || data.durationDays < 1) {
    errors.push('Campaign duration must be at least 1 day');
  }

  // Budget
  if (!data.totalBudget || data.totalBudget <= 0) {
    errors.push('Total budget must be greater than zero');
  }

  // Terms
  if (!data.termsAccepted) {
    errors.push('Terms and conditions must be accepted');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// ================ CAMPAIGN CREATION ================

/**
 * Create a new campaign in Supabase
 * Uses the unified save service to ensure data is saved to saved_campaigns
 */
export const createCampaign = async (campaignData: CampaignData): Promise<CampaignResult> => {
  try {
    logger.info('Creating campaign', { companyName: campaignData.companyName });

    // Validate data
    const validation = validateCampaignData(campaignData);
    if (!validation.isValid) {
      const errorMessage = `Validation failed: ${validation.errors.join(', ')}`;
      logWarning(errorMessage, {
        component: 'campaignCreationService',
        additionalData: { errors: validation.errors }
      });
      return {
        success: false,
        error: new Error(errorMessage)
      };
    }

    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      const errorMessage = 'User not authenticated';
      logError(userError || new Error(errorMessage), {
        component: 'campaignCreationService',
        action: 'getUser'
      });
      return {
        success: false,
        error: new Error(errorMessage)
      };
    }

    // Format dates
    const startDate = dateUtils.formatForStorage(campaignData.startDate);
    const endDate = dateUtils.formatForStorage(campaignData.endDate);

    if (!startDate || !endDate) {
      const errorMessage = 'Invalid date format';
      logError(new Error('Date formatting failed'), {
        component: 'campaignCreationService',
        action: 'formatDates'
      });
      return {
        success: false,
        error: new Error(errorMessage)
      };
    }

    // Use the unified save service to ensure data goes to saved_campaigns
    const saveData: CampaignSaveData = {
      companyName: campaignData.companyName,
      brandName: campaignData.brandName,
      personInCharge: campaignData.personInCharge,
      jobTitle: campaignData.jobTitle,
      email: campaignData.email,
      mobile: campaignData.mobile,
      selectedLocations: campaignData.selectedLocations,
      creativeUrls: campaignData.creativeUrls || [],
      creativeMetadata: (campaignData.creativeUrls || []).map((url, i) => ({
        url,
        creativeId: `creative_${i}`,
        locationName: '',
        orientation: 'landscape' as const,
        scheduledDays: [],
      })),
      totalDevices: campaignData.totalDevices,
      durationDays: campaignData.durationDays,
      startDate,
      endDate,
      totalBudget: campaignData.totalBudget,
      budgetCalculations: campaignData.budgetCalculations,
      termsAccepted: campaignData.termsAccepted,
      paymentType: campaignData.paymentType || 'immediate',
      status: 'pending',
      userId: user.id,
      creatorEmail: user.email || campaignData.email,
    };

    const saveResult = await saveCampaignUnified(saveData);

    if (!saveResult.success) {
      logError(saveResult.error || new Error('Campaign creation failed'), {
        component: 'campaignCreationService',
        action: 'createCampaign',
        additionalData: { errorMessage: saveResult.error?.message }
      });
      return {
        success: false,
        error: saveResult.error || new Error('Failed to create campaign')
      };
    }

    logInfo('Campaign created successfully via unified service', {
      component: 'campaignCreationService',
      additionalData: {
        campaignId: saveResult.campaignId,
        companyName: campaignData.companyName
      }
    });

    return {
      success: true,
      id: saveResult.campaignId,
      campaignId: saveResult.campaignId
    };
  } catch (error) {
    logError(error instanceof Error ? error : new Error('Unexpected error creating campaign'), {
      component: 'campaignCreationService',
      action: 'createCampaign'
    });
    return {
      success: false,
      error: error instanceof Error ? error : new Error('Unknown error occurred')
    };
  }
};

// ================ HOOK ================

/**
 * Hook for campaign creation with loading state
 */
export const useCampaignCreationService = (setCurrentCampaignId?: (id: string) => void) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createParentCampaign = useCallback(async (
    formValues: any,
    selectedLocations: any[],
    creativeUrls: string[],
    totalDevices: number,
    budgetCalculations: any,
    detailsUrl: string | null,
    locationsUrl: string | null,
    durationDays: number,
    companyInfo: any
  ): Promise<CampaignResult> => {
    setIsLoading(true);
    setError(null);

    try {
      // Get dates from localStorage or form
      const startDate = localStorage.getItem('startDate') || formValues.startDate;
      const endDate = localStorage.getItem('endDate') || formValues.endDate;

      const campaignData: CampaignData = {
        companyName: formValues.companyName || companyInfo?.companyName || '',
        brandName: formValues.brandName || companyInfo?.brandName || '',
        personInCharge: formValues.personInCharge || '',
        jobTitle: formValues.jobTitle || '',
        email: formValues.email || '',
        mobile: formValues.mobile || '',
        selectedLocations,
        creativeUrls,
        totalDevices,
        durationDays,
        startDate,
        endDate,
        totalBudget: budgetCalculations?.totalCost || 0,
        budgetCalculations,
        detailsUrl,
        locationsUrl,
        termsAccepted: formValues.termsAccepted || false,
        paymentType: formValues.paymentType || 'immediate'
      };

      const result = await createCampaign(campaignData);

      if (result.success && result.campaignId && setCurrentCampaignId) {
        setCurrentCampaignId(result.campaignId);
      }

      if (!result.success) {
        setError(result.error?.message || 'Failed to create campaign');
      }

      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error occurred');
      setError(error.message);
      logError(error, {
        component: 'campaignCreationService',
        action: 'createParentCampaign'
      });
      return {
        success: false,
        error
      };
    } finally {
      setIsLoading(false);
    }
  }, [setCurrentCampaignId]);

  const handleCampaignCreationResult = useCallback((campaignId: string, childCampaigns: any[], totalCampaigns: number) => {
    // This is just a passthrough for compatibility with existing code
    logger.info('Campaign creation result handled', { campaignId, totalCampaigns });
    return {
      success: true,
      campaignId,
      error: null
    };
  }, []);

  const validateStartDate = useCallback((date: any): boolean => {
    const validation = dateUtils.validateStartDate(date);
    if (!validation.valid) {
      logger.warn('Start date validation failed', { error: validation.error });
    }
    return validation.valid;
  }, []);

  return {
    createCampaign,
    validateCampaignData,
    createParentCampaign,
    handleCampaignCreationResult,
    validateStartDate,
    isLoading,
    error
  };
};
